/**
 * Keyword Data to CSV Conversion
 * Specialized converter for Jungle Scout keyword research data
 */

/**
 * Converts Jungle Scout keyword data to clean CSV format
 * Extracts only the most relevant fields from the attributes object
 * @param {Array} data - Array of keyword objects from Jungle Scout
 * @returns {string} - CSV formatted string
 */
export const keywordDataToCSV = (data) => {
  // Handle empty data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return '';
  }

  // Define columns we want in the CSV (in order)
  const columns = [
    { key: 'keyword', label: 'Keyword' },
    { key: 'monthly_search_volume_exact', label: 'Monthly Search Volume (Exact)' },
    { key: 'monthly_search_volume_broad', label: 'Monthly Search Volume (Broad)' },
    { key: 'monthly_trend', label: 'Monthly Trend %' },
    { key: 'quarterly_trend', label: 'Quarterly Trend %' },
    { key: 'organic_rank', label: 'Organic Rank' },
    { key: 'sponsored_rank', label: 'Sponsored Rank' },
    { key: 'overall_rank', label: 'Overall Rank' },
    { key: 'ppc_bid_exact', label: 'PPC Bid (Exact)' },
    { key: 'ppc_bid_broad', label: 'PPC Bid (Broad)' },
    { key: 'sp_brand_ad_bid', label: 'Sponsored Brand Bid' },
    { key: 'ease_of_ranking_score', label: 'Ease of Ranking Score' },
    { key: 'relevancy_score', label: 'Relevancy Score' },
    { key: 'dominant_category', label: 'Category' },
    { key: 'organic_product_count', label: 'Organic Products' },
    { key: 'sponsored_product_count', label: 'Sponsored Products' },
    { key: 'recommended_promotions', label: 'Recommended Promotions' },
    { key: 'primary_asin', label: 'ASIN' },
    { key: 'country', label: 'Country' },
    { key: 'updated_at', label: 'Updated At' },
  ];

  // Extract data from each keyword object
  const rows = data.map(item => {
    const attrs = item.attributes || {};

    return {
      keyword: attrs.name || '',
      monthly_search_volume_exact: attrs.monthly_search_volume_exact || '',
      monthly_search_volume_broad: attrs.monthly_search_volume_broad || '',
      monthly_trend: attrs.monthly_trend || '',
      quarterly_trend: attrs.quarterly_trend || '',
      organic_rank: attrs.organic_rank || '',
      sponsored_rank: attrs.sponsored_rank || '',
      overall_rank: attrs.overall_rank || '',
      ppc_bid_exact: attrs.ppc_bid_exact || '',
      ppc_bid_broad: attrs.ppc_bid_broad || '',
      sp_brand_ad_bid: attrs.sp_brand_ad_bid || '',
      ease_of_ranking_score: attrs.ease_of_ranking_score || '',
      relevancy_score: attrs.relevancy_score || '',
      dominant_category: attrs.dominant_category || '',
      organic_product_count: attrs.organic_product_count || '',
      sponsored_product_count: attrs.sponsored_product_count || '',
      recommended_promotions: attrs.recommended_promotions || '',
      primary_asin: attrs.primary_asin || '',
      country: attrs.country || '',
      updated_at: attrs.updated_at || '',
    };
  });

  // Escape CSV values
  const escapeCSVValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const stringValue = String(value);

    // If the value contains comma, quotes, or newlines, wrap in quotes and escape existing quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  };

  // Create header row
  const headerRow = columns.map(col => escapeCSVValue(col.label)).join(',');

  // Create data rows
  const dataRows = rows.map(row => {
    return columns.map(col => escapeCSVValue(row[col.key])).join(',');
  });

  // Combine header and data rows
  return [headerRow, ...dataRows].join('\n');
};

/**
 * Downloads keyword data as CSV
 * @param {string} csvContent - The CSV content to download
 * @param {string} filename - The filename for the download
 */
export const downloadKeywordCSV = (csvContent, filename = 'keyword-data.csv') => {
  // Ensure filename ends with .csv
  if (!filename.endsWith('.csv')) {
    filename += '.csv';
  }

  // Create a blob with the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a temporary download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  // Append to document, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
};

/**
 * Converts keyword data to CSV and triggers download
 * @param {Array} data - Array of keyword objects
 * @param {string} filename - The filename for the download
 */
export const convertAndDownloadKeywordData = (data, filename = 'keyword-data.csv') => {
  const csvContent = keywordDataToCSV(data);
  if (csvContent) {
    downloadKeywordCSV(csvContent, filename);
  } else {
    console.error('No keyword data to convert to CSV');
  }
};

export default {
  keywordDataToCSV,
  downloadKeywordCSV,
  convertAndDownloadKeywordData
};
